// =============================================================================
// VandieZ admin-web -- System health (/system-health)
// =============================================================================
// Dashboard view of the platform's service-level health: DB pool,
// Redis ping, recent event volume, error counts. Polls the new
// /admin/system/health endpoint every 30s.
//
// When something looks off (Redis unreachable, fcm_failures spike,
// connection pool exhausted) the relevant card flips to a red/amber
// tone so the operator notices.
// =============================================================================

import {
  AlertCircle,
  Database,
  HardDrive,
  RefreshCw,
  Server,
  TrendingUp,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { systemAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  DbStatsResponse,
  MigrationsResponse,
  SystemHealthResponse,
  SystemVersionResponse,
} from "@/types/system";

const POLL_MS = 30_000;

function pingTone(ms: number, warnAbove: number, alertAbove: number): string {
  if (ms >= alertAbove) {return "text-danger-subtle-foreground";}
  if (ms >= warnAbove) {return "text-warning-subtle-foreground";}
  return "text-success-subtle-foreground";
}

function countTone(n: number, warnAbove: number): string {
  if (n >= warnAbove) {return "text-danger-subtle-foreground";}
  return "text-foreground";
}

export function SystemHealthPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [data, setData] = useState<SystemHealthResponse | null>(null);
  const [version, setVersion] = useState<SystemVersionResponse | null>(null);
  const [migrations, setMigrations] = useState<MigrationsResponse | null>(
    null,
  );
  const [dbStats, setDbStats] = useState<DbStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      try {
        const res = await systemAdminApi.health(accessToken, signal);
        setData(res);
        setError(null);
        // Version + migrations are best-effort + only need one fetch.
        if (version === null) {
          void systemAdminApi
            .version(accessToken, signal)
            .then(setVersion)
            .catch(() => undefined);
        }
        if (migrations === null) {
          void systemAdminApi
            .migrations(accessToken, signal)
            .then(setMigrations)
            .catch(() => undefined);
        }
        if (dbStats === null) {
          void systemAdminApi
            .dbStats(accessToken, signal)
            .then(setDbStats)
            .catch(() => undefined);
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load health.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchHealth(c.signal);
    const id = window.setInterval(() => { void fetchHealth(); }, POLL_MS);
    return () => {
      c.abort();
      window.clearInterval(id);
    };
  }, [fetchHealth]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="System health"
        description="Live service-level signals. Polls every 30s."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchHealth()}
            disabled={loading}
          >
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {data === null ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Loading…
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-4 w-4" /> Database
                </CardTitle>
                <CardDescription>
                  Postgres connection pool + ping latency.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">
                    Ping
                  </span>
                  <span
                    className={cn(
                      "text-2xl font-semibold tabular-nums",
                      pingTone(data.db.ping_ms, 100, 500),
                    )}
                  >
                    {data.db.ping_ms} ms
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">
                    Connections
                  </span>
                  <span className="font-mono">
                    {data.db.connections_active} active ·{" "}
                    {data.db.connections_idle} idle
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card
              className={
                !data.redis.reachable
                  ? "border-danger"
                  : ""
              }
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="h-4 w-4" /> Redis
                </CardTitle>
                <CardDescription>
                  Cache + rate-limit + session store.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!data.redis.reachable ? (
                  <p className="text-danger-subtle-foreground text-sm font-medium">
                    Unreachable — investigate immediately.
                  </p>
                ) : (
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-muted-foreground">
                      Ping
                    </span>
                    <span
                      className={cn(
                        "text-2xl font-semibold tabular-nums",
                        pingTone(data.redis.ping_ms, 50, 200),
                      )}
                    >
                      {data.redis.ping_ms} ms
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4" /> Traffic
              </CardTitle>
              <CardDescription>
                Event volume + error counts. Cards turn red when
                breaching the warning threshold.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
              <KpiBlock
                label="Events / 1h"
                value={data.traffic.events_last_1h}
                tone="text-foreground"
                icon={<Server className="h-4 w-4 text-muted-foreground" />}
              />
              <KpiBlock
                label="Errors / 1h"
                value={data.traffic.errors_last_1h}
                tone={countTone(data.traffic.errors_last_1h, 50)}
                icon={
                  <AlertCircle
                    className={cn(
                      "h-4 w-4",
                      data.traffic.errors_last_1h >= 50
                        ? "text-danger"
                        : "text-muted-foreground",
                    )}
                  />
                }
              />
              <KpiBlock
                label="FCM fails / 1h"
                value={data.traffic.fcm_failures_last_1h}
                tone={countTone(data.traffic.fcm_failures_last_1h, 10)}
                icon={
                  <AlertCircle
                    className={cn(
                      "h-4 w-4",
                      data.traffic.fcm_failures_last_1h >= 10
                        ? "text-danger"
                        : "text-muted-foreground",
                    )}
                  />
                }
              />
              <KpiBlock
                label="Notifications / 24h"
                value={data.traffic.notifications_last_24h}
                tone="text-foreground"
                icon={
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                }
              />
            </CardContent>
          </Card>

          {version !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Build / runtime</CardTitle>
                <CardDescription>
                  What's actually live in the backend container right now.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Build SHA
                  </div>
                  <div className="font-mono">{version.build_sha}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    App version
                  </div>
                  <div className="font-mono">{version.app_version}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Environment
                  </div>
                  <div className="font-mono">{version.environment}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Python
                  </div>
                  <div className="font-mono">{version.python_version}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Process started
                  </div>
                  <div className="font-mono text-xs">
                    {new Date(version.process_started_at).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Uptime
                  </div>
                  <div className="font-mono">
                    {Math.floor(version.uptime_seconds / 3600)}h{" "}
                    {Math.floor((version.uptime_seconds % 3600) / 60)}m
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {dbStats !== null && dbStats.tables.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">DB table sizes</CardTitle>
                <CardDescription>
                  Approximate row counts from pg_stat_user_tables —
                  fast, no full-table scan.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 text-sm">
                {dbStats.tables.map((t) => (
                  <div key={t.name}>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {t.name}
                    </div>
                    <div className="font-mono tabular-nums text-lg">
                      {t.row_count.toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {migrations !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Migrations applied</CardTitle>
                <CardDescription>
                  Alembic head revisions. Verify "is migration X live?"
                  without ssh.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {migrations.applied.map((r) => (
                    <span
                      key={r}
                      className="font-mono text-xs rounded bg-muted px-2 py-1"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground">
            Snapshot as of {new Date(data.as_of).toLocaleString()}. Auto-refreshing every 30s.
          </p>
        </>
      )}
    </div>
  );
}

function KpiBlock({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: string;
  icon: React.JSX.Element;
}): React.JSX.Element {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        {icon}
        {label}
      </div>
      <p className={cn("text-3xl font-semibold tabular-nums", tone)}>
        {value.toLocaleString("en-IN")}
      </p>
    </div>
  );
}
